import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Images, Home as HomeIcon, CalendarDays, MessageSquare,
  UploadCloud, Trash2, Plus, Loader2, Star, ImagePlus,
} from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const TABS = [
  { id: "fotos", label: "Fotos", icon: Images },
  { id: "casa", label: "Casa", icon: HomeIcon },
  { id: "reservas", label: "Reservas", icon: CalendarDays },
  { id: "comentarios", label: "Comentários", icon: MessageSquare },
];

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-clay";
const labelCls = "mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-fog";

const STATUS = ["novo", "contactado", "confirmado", "recusado"];
const STATUS_STYLES = {
  novo: "bg-clay/10 text-clay",
  contactado: "bg-blue-500/10 text-blue-600",
  confirmado: "bg-green-600/10 text-green-700",
  recusado: "bg-red-500/10 text-red-600",
};

export default function Admin() {
  const [tab, setTab] = useState("fotos");
  const [properties, setProperties] = useState([]);
  const [current, setCurrent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);

  const loadProperties = useCallback(async () => {
    const { data } = await api.get("/properties");
    setProperties(data);
    setCurrent((c) => (c ? data.find((p) => p.id === c.id) || data[0] : data[0]));
  }, []);

  const loadPhotos = useCallback(async (propId) => {
    if (!propId) return;
    const { data } = await api.get(`/properties/${propId}/photos`);
    setPhotos(data.map((p) => ({ ...p, url: fileUrl(p.storage_path) })));
  }, []);

  const loadBookings = useCallback(async () => {
    const { data } = await api.get("/bookings");
    setBookings(data);
  }, []);

  const loadReviews = useCallback(async () => {
    const { data } = await api.get("/reviews?all=true");
    setReviews(data);
  }, []);

  useEffect(() => {
    loadProperties().catch(() => toast.error("Erro ao carregar as casas"));
    loadBookings().catch(() => {});
    loadReviews().catch(() => {});
  }, [loadProperties, loadBookings, loadReviews]);

  useEffect(() => {
    if (current?.id) loadPhotos(current.id).catch(() => {});
  }, [current?.id, loadPhotos]);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-cream">
      <header className="border-b border-line bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <div className="flex items-center gap-4">
            <Link data-testid="admin-back-link" to="/" className="rounded-full border border-line p-2 text-fog transition-colors duration-300 hover:text-clay">
              <ArrowLeft size={16} />
            </Link>
            <h1 className="font-display text-xl text-ink">Gestão — Queluz <span className="text-clay">Cardoso</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="admin-property-select"
              value={current?.id || ""}
              onChange={(e) => setCurrent(properties.find((p) => p.id === e.target.value))}
              className={`${inputCls} max-w-[220px]`}
            >
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              data-testid="admin-new-property-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/properties");
                  await loadProperties();
                  setCurrent(data);
                  toast.success("Nova casa criada — edite os detalhes no separador Casa.");
                } catch { toast.error("Erro ao criar casa"); }
              }}
              className="flex items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 text-xs font-medium text-cream transition-colors duration-300 hover:bg-clay"
            >
              <Plus size={14} /> Nova casa
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5 sm:px-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors duration-300 ${
                tab === id ? "border-clay text-ink" : "border-transparent text-fog hover:text-ink"
              }`}
            >
              <Icon size={15} /> {label}
              {id === "reservas" && bookings.filter((b) => b.status === "novo").length > 0 && (
                <span className="rounded-full bg-clay px-2 py-0.5 text-[10px] font-semibold text-cream" data-testid="new-bookings-badge">
                  {bookings.filter((b) => b.status === "novo").length}
                </span>
              )}
              {id === "comentarios" && reviews.filter((r) => !r.approved).length > 0 && (
                <span className="rounded-full bg-clay px-2 py-0.5 text-[10px] font-semibold text-cream" data-testid="pending-reviews-badge">
                  {reviews.filter((r) => !r.approved).length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        {tab === "fotos" && <PhotosTab property={current} photos={photos} reload={() => loadPhotos(current?.id)} reloadProperties={loadProperties} />}
        {tab === "casa" && <PropertyTab property={current} photos={photos} onSaved={loadProperties} />}
        {tab === "reservas" && <BookingsTab bookings={bookings} reload={loadBookings} property={current} />}
        {tab === "comentarios" && <ReviewsTab reviews={reviews} reload={loadReviews} />}
      </main>
    </div>
  );
}

function PhotosTab({ property, photos, reload, reloadProperties }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!files.length || !property) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/properties/${property.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} foto(s) carregada(s)!`);
      setFiles([]);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const setCover = async (photo) => {
    try {
      await api.put(`/properties/${property.id}`, { hero_image: photo.url });
      toast.success("Imagem de capa atualizada!");
      reloadProperties();
    } catch { toast.error("Erro ao definir capa"); }
  };

  const remove = async (photo) => {
    try {
      await api.delete(`/photos/${photo.id}`);
      toast.success("Foto removida");
      reload();
    } catch { toast.error("Erro ao remover foto"); }
  };

  return (
    <div data-testid="photos-tab">
      <div className="mb-8 rounded-2xl border-2 border-dashed border-line bg-white p-8 text-center transition-colors duration-300 hover:border-clay/50">
        <UploadCloud size={32} className="mx-auto mb-3 text-clay" />
        <p className="mb-1 font-display text-lg italic text-ink">Carregar várias fotos de uma vez</p>
        <p className="mb-5 text-sm text-fog">JPEG, PNG ou WebP · até 30 fotos · máx. 12 MB cada</p>
        <input
          data-testid="photo-upload-input"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="mx-auto block text-sm text-fog file:mr-4 file:rounded-full file:border-0 file:bg-ink file:px-5 file:py-2.5 file:text-xs file:font-medium file:text-cream hover:file:bg-clay"
        />
        {files.length > 0 && (
          <button
            data-testid="photo-upload-btn"
            onClick={upload}
            disabled={uploading}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3 text-sm font-medium text-cream transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-clay-dark disabled:opacity-60"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
            {uploading ? "A carregar..." : `Carregar ${files.length} foto(s)`}
          </button>
        )}
      </div>

      {photos.length === 0 ? (
        <p className="py-10 text-center text-sm text-fog" data-testid="photos-empty">
          Ainda não há fotos. Enquanto isso, o site mostra imagens de demonstração.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="photos-grid">
          {photos.map((photo) => (
            <div key={photo.id} className="group relative overflow-hidden rounded-xl border border-line bg-white" data-testid={`photo-item-${photo.id}`}>
              <img src={photo.url} alt={photo.original_filename} className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="flex items-center justify-between gap-1 p-2">
                <button
                  data-testid={`photo-cover-${photo.id}`}
                  onClick={() => setCover(photo)}
                  className="rounded-full border border-line px-3 py-1 text-[11px] text-fog transition-colors duration-300 hover:border-clay hover:text-clay"
                >
                  Definir capa
                </button>
                <button
                  data-testid={`photo-delete-${photo.id}`}
                  onClick={() => remove(photo)}
                  className="rounded-full p-1.5 text-fog transition-colors duration-300 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PropertyTab({ property, photos, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (property) {
      setForm({
        ...property,
        amenities: (property.amenities || []).join("\n"),
        rules: (property.rules || []).join("\n"),
        payment_methods: (property.payment_methods || []).join("\n"),
      });
    }
  }, [property]);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${property.id}`, {
        name: form.name, location: form.location, type: form.type,
        area_m2: Number(form.area_m2), bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms), floor: form.floor,
        max_guests: Number(form.max_guests), tagline: form.tagline,
        description: form.description, hero_image: form.hero_image,
        whatsapp: form.whatsapp, phone: form.phone || "", price_note: form.price_note,
        email: form.email || "", address: form.address || "",
        price_per_night: form.price_per_night === "" || form.price_per_night == null ? null : Number(form.price_per_night),
        amenities: form.amenities.split("\n").map((s) => s.trim()).filter(Boolean),
        rules: form.rules.split("\n").map((s) => s.trim()).filter(Boolean),
        payment_methods: form.payment_methods.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Casa atualizada!");
      onSaved();
    } catch { toast.error("Erro ao guardar"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="property-tab" className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><span className={labelCls}>Nome</span><input data-testid="prop-name-input" className={inputCls} value={form.name} onChange={set("name")} /></div>
          <div><span className={labelCls}>Localização</span><input data-testid="prop-location-input" className={inputCls} value={form.location} onChange={set("location")} /></div>
          <div><span className={labelCls}>Tipo</span><input data-testid="prop-type-input" className={inputCls} value={form.type} onChange={set("type")} /></div>
          <div><span className={labelCls}>Andar</span><input data-testid="prop-floor-input" className={inputCls} value={form.floor} onChange={set("floor")} /></div>
          <div><span className={labelCls}>Área (m²)</span><input data-testid="prop-area-input" type="number" className={inputCls} value={form.area_m2} onChange={set("area_m2")} /></div>
          <div><span className={labelCls}>Quartos</span><input data-testid="prop-bedrooms-input" type="number" className={inputCls} value={form.bedrooms} onChange={set("bedrooms")} /></div>
          <div><span className={labelCls}>Casas de banho</span><input data-testid="prop-bathrooms-input" type="number" className={inputCls} value={form.bathrooms} onChange={set("bathrooms")} /></div>
          <div><span className={labelCls}>Máx. hóspedes</span><input data-testid="prop-guests-input" type="number" className={inputCls} value={form.max_guests} onChange={set("max_guests")} /></div>
        </div>
        <div><span className={labelCls}>Slogan</span><input data-testid="prop-tagline-input" className={inputCls} value={form.tagline} onChange={set("tagline")} /></div>
        <div><span className={labelCls}>Descrição</span><textarea data-testid="prop-description-input" rows={5} className={`${inputCls} resize-y`} value={form.description} onChange={set("description")} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><span className={labelCls}>WhatsApp (ex.: +351910000000)</span><input data-testid="prop-whatsapp-input" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} /></div>
          <div><span className={labelCls}>Telefone</span><input data-testid="prop-phone-input" className={inputCls} value={form.phone || ""} onChange={set("phone")} /></div>
          <div><span className={labelCls}>Email</span><input data-testid="prop-email-input" className={inputCls} value={form.email || ""} onChange={set("email")} /></div>
          <div><span className={labelCls}>Morada</span><input data-testid="prop-address-input" className={inputCls} value={form.address || ""} onChange={set("address")} /></div>
          <div><span className={labelCls}>Preço por noite (€)</span><input data-testid="prop-price-night-input" type="number" min="0" className={inputCls} value={form.price_per_night ?? ""} onChange={set("price_per_night")} /></div>
          <div><span className={labelCls}>Nota de preço</span><input data-testid="prop-price-input" className={inputCls} value={form.price_note} onChange={set("price_note")} /></div>
        </div>
        <div><span className={labelCls}>Imagem de capa (URL) — ou use “Definir capa” no separador Fotos</span><input data-testid="prop-hero-input" className={inputCls} value={form.hero_image} onChange={set("hero_image")} /></div>
        <div><span className={labelCls}>Comodidades (uma por linha)</span><textarea data-testid="prop-amenities-input" rows={6} className={`${inputCls} resize-y`} value={form.amenities} onChange={set("amenities")} /></div>
        <div><span className={labelCls}>Regras (uma por linha)</span><textarea data-testid="prop-rules-input" rows={4} className={`${inputCls} resize-y`} value={form.rules} onChange={set("rules")} /></div>
        <div><span className={labelCls}>Métodos de pagamento (um por linha)</span><textarea data-testid="prop-payments-input" rows={2} className={`${inputCls} resize-y`} value={form.payment_methods} onChange={set("payment_methods")} /></div>
        <button
          data-testid="prop-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-clay px-8 py-3.5 text-sm font-medium text-cream transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-clay-dark disabled:opacity-60"
        >
          {saving && <Loader2 size={15} className="animate-spin" />} Guardar alterações
        </button>
      </div>
      <div>
        <span className={labelCls}>Capa atual</span>
        <img src={form.hero_image} alt="Capa" className="aspect-[4/3] w-full rounded-2xl border border-line object-cover" data-testid="prop-hero-preview" />
        <p className="mt-3 text-xs leading-relaxed text-fog">
          Tem {photos.length} foto(s) carregada(s). No separador Fotos pode carregar várias de uma vez e escolher a capa.
        </p>
      </div>
    </div>
  );
}

function BookingsTab({ bookings, reload, property }) {
  const [block, setBlock] = useState({ check_in: "", check_out: "", name: "" });
  const [blocking, setBlocking] = useState(false);
  const [filter, setFilter] = useState("todos");
  const filtered = filter === "todos" ? bookings : bookings.filter((b) => b.status === filter);
  const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
  const nights = (b) => Math.max(0, Math.round((new Date(b.check_out) - new Date(b.check_in)) / 86400000));

  const setStatus = async (b, status) => {
    try {
      await api.patch(`/bookings/${b.id}`, { status });
      toast.success(status === "confirmado" ? "Reserva confirmada — datas bloqueadas no calendário." : "Estado atualizado");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar"); }
  };
  const remove = async (b) => {
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Pedido removido");
      reload();
    } catch { toast.error("Erro ao remover"); }
  };
  const addBlock = async () => {
    if (!block.check_in || !block.check_out) {
      toast.error("Indique as datas a bloquear.");
      return;
    }
    setBlocking(true);
    try {
      await api.post("/bookings", {
        property_id: property.id, name: block.name || "Bloqueio manual", contact: "admin",
        check_in: block.check_in, check_out: block.check_out, guests: 1, message: "Datas bloqueadas pela gestão", status: "confirmado",
      });
      toast.success("Datas bloqueadas no calendário.");
      setBlock({ check_in: "", check_out: "", name: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao bloquear"); } finally { setBlocking(false); }
  };

  return (
    <div className="grid gap-6">
      <div className="rounded-2xl border border-line bg-white p-5" data-testid="block-dates-panel">
        <h3 className="mb-1 font-display text-lg text-ink">Bloquear datas</h3>
        <p className="mb-4 text-xs text-fog">Para reservas feitas fora do site (telefone, Airbnb…). Ficam marcadas como ocupadas no calendário público.</p>
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1.4fr_auto] sm:items-end">
          <div><span className={labelCls}>Check-in</span><input data-testid="block-checkin-input" type="date" className={inputCls} value={block.check_in} onChange={(e) => setBlock((b) => ({ ...b, check_in: e.target.value }))} /></div>
          <div><span className={labelCls}>Check-out</span><input data-testid="block-checkout-input" type="date" className={inputCls} value={block.check_out} onChange={(e) => setBlock((b) => ({ ...b, check_out: e.target.value }))} /></div>
          <div><span className={labelCls}>Nome (opcional)</span><input data-testid="block-name-input" className={inputCls} placeholder="Ex.: Família Silva (Airbnb)" value={block.name} onChange={(e) => setBlock((b) => ({ ...b, name: e.target.value }))} /></div>
          <button data-testid="block-dates-btn" onClick={addBlock} disabled={blocking}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-cream transition-colors duration-300 hover:bg-clay disabled:opacity-60">
            {blocking ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />} Bloquear
          </button>
        </div>
      </div>
      {bookings.length === 0 ? (
        <p className="py-10 text-center text-sm text-fog" data-testid="bookings-empty">Ainda não há pedidos de reserva.</p>
      ) : (
    <div className="rounded-2xl border border-line bg-white" data-testid="bookings-table">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <p className="text-sm text-fog" data-testid="bookings-count">
          <span className="font-semibold text-ink">{filtered.length}</span> de {bookings.length} pedido(s)
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="bookings-filter">
          {["todos", ...STATUS].map((s) => (
            <button key={s} data-testid={`bookings-filter-${s}`} onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors duration-300 ${
                filter === s ? "bg-ink text-cream" : "bg-sand text-fog hover:text-ink"
              }`}>
              {s} {s !== "todos" && `(${bookings.filter((b) => b.status === s).length})`}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-[0.12em] text-fog">
            <th className="px-5 py-4">Recebido</th>
            <th className="px-5 py-4">Hóspede</th>
            <th className="px-5 py-4">Contacto</th>
            <th className="px-5 py-4">Datas</th>
            <th className="px-5 py-4">Mensagem</th>
            <th className="px-5 py-4">Estado</th>
            <th className="px-5 py-4" />
          </tr>
        </thead>
        <tbody>
          {filtered.map((b) => (
            <tr key={b.id} className="border-b border-line align-top last:border-0" data-testid={`booking-row-${b.id}`}>
              <td className="whitespace-nowrap px-5 py-4 text-xs text-fog">{formatDate(b.created_at)}</td>
              <td className="px-5 py-4">
                <p className="font-medium text-ink">{b.name}</p>
                <p className="text-xs text-fog">{b.guests} {b.guests === 1 ? "hóspede" : "hóspedes"}</p>
              </td>
              <td className="px-5 py-4 text-fog">
                {b.email && <a href={`mailto:${b.email}`} className="block hover:text-clay" data-testid={`booking-email-${b.id}`}>{b.email}</a>}
                {b.phone && <a href={`tel:${b.phone}`} className="block hover:text-clay" data-testid={`booking-phone-${b.id}`}>{b.phone}</a>}
                {!b.email && !b.phone && <span>{b.contact || "—"}</span>}
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-fog">
                {b.check_in} → {b.check_out}
                <p className="text-xs text-fog/70">{nights(b)} {nights(b) === 1 ? "noite" : "noites"}</p>
              </td>
              <td className="max-w-[260px] px-5 py-4 text-fog">
                <p className="whitespace-pre-wrap break-words">{b.message || "—"}</p>
              </td>
              <td className="px-5 py-4">
                <select
                  data-testid={`booking-status-${b.id}`}
                  value={b.status}
                  onChange={(e) => setStatus(b, e.target.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium outline-none ${STATUS_STYLES[b.status] || ""}`}
                >
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-5 py-4">
                <button data-testid={`booking-delete-${b.id}`} onClick={() => remove(b)}
                  className="rounded-full p-1.5 text-fog transition-colors duration-300 hover:bg-red-50 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
      )}
    </div>
  );
}

function ReviewsTab({ reviews, reload }) {
  const [form, setForm] = useState({ author: "", source: "Google", rating: 5, text: "" });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Indique o autor e o comentário.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/reviews", { ...form, rating: Number(form.rating) });
      toast.success("Comentário adicionado");
      setForm({ author: "", source: "Google", rating: 5, text: "" });
      reload();
    } catch { toast.error("Erro ao adicionar"); } finally { setSaving(false); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Comentário removido");
      reload();
    } catch { toast.error("Erro ao remover"); }
  };
  const toggle = async (r) => {
    try {
      await api.patch(`/reviews/${r.id}`, { approved: !r.approved });
      toast.success(r.approved ? "Comentário ocultado" : "Comentário publicado no site");
      reload();
    } catch { toast.error("Erro ao atualizar"); }
  };

  return (
    <div data-testid="reviews-tab" className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div className="h-fit rounded-2xl border border-line bg-white p-6">
        <h3 className="mb-4 font-display text-lg italic text-ink">Adicionar comentário</h3>
        <div className="grid gap-3">
          <input data-testid="review-author-input" placeholder="Autor (ex.: Maria, via Google)" className={inputCls}
            value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="review-source-select" className={inputCls} value={form.source}
              onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
              {["Google", "Airbnb", "Trustpilot", "Booking"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <select data-testid="review-rating-select" className={inputCls} value={form.rating}
              onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} estrelas</option>)}
            </select>
          </div>
          <textarea data-testid="review-text-input" rows={4} placeholder="Texto do comentário" className={`${inputCls} resize-y`}
            value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} />
          <button data-testid="review-add-btn" onClick={add} disabled={saving}
            className="inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors duration-300 hover:bg-clay disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Adicionar
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {reviews.length === 0 && (
          <p className="py-10 text-center text-sm text-fog" data-testid="reviews-empty">Ainda não há comentários. Os que os hóspedes deixarem no site aparecem aqui para aprovação.</p>
        )}
        {reviews.map((r) => (
          <div key={r.id} className={`flex items-start justify-between gap-4 rounded-2xl border bg-white p-5 ${r.approved ? "border-line" : "border-clay/40 bg-clay/[0.03]"}`} data-testid={`review-item-${r.id}`}>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink">{r.author}</span>
                <span className="rounded-full bg-sand px-2.5 py-0.5 text-[10px] font-medium text-fog">{r.source === "Site" ? "Hóspede (site)" : r.source}</span>
                <span className="flex gap-0.5">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={11} className="fill-clay text-clay" />
                  ))}
                </span>
                <span data-testid={`review-state-${r.id}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ${r.approved ? "bg-green-600/10 text-green-700" : "bg-clay/10 text-clay"}`}>
                  {r.approved ? "publicado" : "por aprovar"}
                </span>
              </div>
              <p className="text-sm text-fog">{r.text}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button data-testid={`review-toggle-${r.id}`} onClick={() => toggle(r)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors duration-300 ${r.approved ? "border border-line text-fog hover:text-ink" : "bg-ink text-cream hover:bg-clay"}`}>
                {r.approved ? "Ocultar" : "Aprovar"}
              </button>
              <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)}
                className="rounded-full p-1.5 text-fog transition-colors duration-300 hover:bg-red-50 hover:text-red-600">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
